import type {SetFieldsOperation} from '@github-ui/optimistic-connection'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import type {
  AddReactionMutationMutation,
  AddReactionMutationMutationVariables,
} from '@github-ui/server-persisted-queries/types/addReactionMutation'
import {useCallback} from 'react'
import type {Environment} from 'relay-runtime'

import {
  applyOptimisticReaction,
  completeReactionMutation,
  isLatestReactionMutation,
  type ReactionMutationCallbacks,
  type ReactionMutationContext,
  startReactionMutation,
  submitReactionMutation,
  writeReactionSubjectToRelay,
} from './reaction-mutation'

type AddReactionSubject = NonNullable<NonNullable<AddReactionMutationMutation['addReaction']>['subject']>
type AddReactionVariables = AddReactionMutationMutationVariables & ReactionMutationCallbacks<AddReactionSubject>
type AddReactionOperation = SetFieldsOperation & {
  reactionMutation: {variables: AddReactionVariables; token: symbol}
}

export function useAddReactionMutation(environment: Environment) {
  const mutation = useEntityMutation<AddReactionSubject, Error, ReactionMutationContext>({
    mutationFn: async operation => {
      const {variables} = (operation as AddReactionOperation).reactionMutation
      const data = await submitReactionMutation<AddReactionMutationMutation>('POST', variables)
      const subject = data.addReaction?.subject
      if (!subject) throw new Error('Add reaction mutation returned no subject')
      return subject
    },
    getConfirmedOperation: (subject, operation) => {
      const {variables, token} = (operation as AddReactionOperation).reactionMutation
      return subject.reactionGroups && isLatestReactionMutation(environment, String(variables.input.subjectId), token)
        ? {type: 'setFields', id: subject.id, fields: {reactionGroups: subject.reactionGroups}}
        : undefined
    },
    onMutate: ({operation}) => {
      const {variables, token} = (operation as AddReactionOperation).reactionMutation
      return {
        optimisticUpdate: applyOptimisticReaction(environment, variables, true),
        mutation: startReactionMutation(environment, String(variables.input.subjectId), token),
      }
    },
    onSuccess: (subject, {operation}, context) => {
      const {variables} = (operation as AddReactionOperation).reactionMutation
      context?.optimisticUpdate.dispose()
      if (!context || !completeReactionMutation(environment, String(variables.input.subjectId), context.mutation)) {
        variables.onStale?.()
        return
      }

      writeReactionSubjectToRelay(environment, subject)
      variables.onCompleted?.(subject)
    },
    onError: (error, {operation}, context) => {
      const {variables} = (operation as AddReactionOperation).reactionMutation
      context?.optimisticUpdate.dispose()
      if (!context || !completeReactionMutation(environment, String(variables.input.subjectId), context.mutation)) {
        variables.onStale?.()
        return
      }

      variables.onError?.(error)
    },
  })

  const mutate = useCallback(
    (variables: AddReactionVariables) => {
      const operation: AddReactionOperation = {
        type: 'setFields',
        id: String(variables.input.subjectId),
        fields: variables.optimisticFields ?? {},
        reactionMutation: {variables, token: Symbol()},
      }
      mutation.mutate({operation})
    },
    [mutation],
  )

  return {...mutation, mutate}
}
